import { useEffect, useRef, useState } from 'react';

import {
  getSharedDCCTVolumeInfo,
  MedicalImageRender,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type {
  FullHeadMPREngineSession,
  ToothBasisSeriesFrames,
  ToothSliceSource,
} from '../config';
import {
  makeToothFocusEnginePayload,
  resolveToothSliceThicknessOptionsMm,
} from '../lib';

import { useResolvedToothID } from './useResolvedToothID';
import { useToothBasisSeries } from './useToothBasisSeries';
import { useToothSliceOptions } from './useToothSliceOptions';
import { useToothSliceSourceState } from './useToothSliceSource';

type UseToothSliceFocusParams = {
  engineSession: FullHeadMPREngineSession | null;
  toothID?: string;
};

export const useToothSliceFocus = (params: UseToothSliceFocusParams) => {
  const { engineSession, toothID } = params;

  const focusToothID = useResolvedToothID(toothID);
  const sourceState = useToothSliceSourceState(toothID);
  const contextKey = engineSession
    ? `${engineSession.id}:${focusToothID}`
    : null;
  const toothInput =
    sourceState.source?.kind === 'tooth' ? sourceState.source : null;
  const thicknessOptionsMm = resolveToothSliceThicknessOptionsMm(
    toothInput ? getSharedDCCTVolumeInfo(toothInput.volumeFileID) : null,
  );
  const { sliceOptions } = useToothSliceOptions({
    thicknessOptionsMm,
    toothID: focusToothID,
  });
  const { frames, loading } = useToothBasisSeries({
    input: toothInput,
    isEnabled: Boolean(engineSession),
    sliceOptions,
  });

  const [readyContextKey, setReadyContextKey] = useState<string | null>(null);
  const initializedContextRef = useRef<string | null>(null);
  const presentationIDRef = useRef<string | null>(null);
  const initializedSourceRef = useRef<{
    contextKey: string;
    frames: ToothBasisSeriesFrames | null;
    source: ToothSliceSource | null;
  } | null>(null);
  const sourceStateRef = useRef(sourceState);
  sourceStateRef.current = sourceState;
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;

  useEffect(() => {
    initializedContextRef.current = null;
    presentationIDRef.current = null;
    setReadyContextKey(null);

    if (!engineSession || !contextKey) {
      return;
    }

    return MedicalImageRender.addEventListener(
      'fullHeadMPRPresentationReady',
      (event) => {
        if (
          contextKeyRef.current === contextKey &&
          event.volumeFileID === engineSession.volumeFileID &&
          event.presentationID === presentationIDRef.current
        ) {
          setReadyContextKey(contextKey);
        }
      },
      'fullHeadMPR',
    );
  }, [engineSession, contextKey]);

  const applyFocus = () => {
    if (!engineSession || !contextKey) {
      return;
    }

    const currentSource = sourceStateRef.current.source;
    const initializedSource =
      initializedSourceRef.current?.contextKey === contextKey
        ? initializedSourceRef.current
        : null;
    const snapshot =
      currentSource && (currentSource.kind !== 'tooth' || frames)
        ? { source: currentSource, frames }
        : initializedSource;

    if (currentSource?.kind === 'tooth' && !snapshot) {
      return;
    }

    const source = snapshot?.source ?? null;
    const seriesFrames = snapshot?.frames ?? null;

    if (source && source.volumeFileID !== engineSession.volumeFileID) {
      return;
    }

    const presentationID = crypto.randomUUID();
    initializedSourceRef.current = { contextKey, source, frames: seriesFrames };
    presentationIDRef.current = presentationID;
    initializedContextRef.current = contextKey;
    setReadyContextKey(null);

    if (source && source.volumeFileID === engineSession.volumeFileID) {
      MedicalImageRender.setFullHeadMPRToothFocus({
        ...(source.kind === 'tooth'
          ? {
              ...makeToothFocusEnginePayload(source),
              seriesFrames: seriesFrames ?? undefined,
            }
          : { origin: source.origin }),
        presentationID,
      });
    } else {
      MedicalImageRender.resetFullHeadMPRView({ presentationID });
    }
  };
  const applyFocusRef = useRef(applyFocus);
  applyFocusRef.current = applyFocus;

  useEffect(() => {
    if (
      !contextKey ||
      initializedContextRef.current === contextKey ||
      (focusToothID && sourceState.status !== 'succeeded')
    ) {
      return;
    }

    applyFocusRef.current();
  }, [
    contextKey,
    focusToothID,
    frames,
    sourceState.status,
    sourceState.source?.volumeFileID,
  ]);

  const resetFocus = () => {
    MedicalImageRender.resetFullHeadMPRView();
    applyFocus();
  };

  return {
    isReady: Boolean(contextKey && readyContextKey === contextKey),
    isFailed:
      initializedContextRef.current !== contextKey &&
      (sourceState.status === 'failed' ||
        Boolean(toothInput && loading === 'failed')),
    resetFocus,
  };
};
